import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  AlertTriangle,
  BarChart3,
  Briefcase,
  Calendar,
  CheckCircle,
  Filter,
  Link,
  Plus,
  RefreshCw,
  Search,
  Sparkles,
  Star,
  Target,
  User,
  Users,
  Wand2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const getEmployeeName = (employee) => employee?.nome_completo || employee?.nome || 'Sem nome';
const getEmployeeRole = (employee) => employee?.posicao || employee?.cargo || 'Sem cargo';
const getEmployeeDepartment = (employee) => {
  if (employee?.departamento) {
    return employee.departamento;
  }
  if (Array.isArray(employee?.departamentos) && employee.departamentos.length > 0) {
    return employee.departamentos.filter(Boolean).join(', ');
  }
  return 'Sem departamento';
};

const formatDate = (value) => {
  if (!value) {
    return 'Sem data';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return 'Sem data';
  }
  return parsed.toLocaleDateString('pt-BR');
};

const getBatchStatusMeta = (status) => {
  if (status === 'novo') {
    return {
      label: 'Novo vínculo',
      tone: 'border-emerald-200 bg-emerald-50 text-emerald-700',
      hint: 'Colaborador sem DCF ativo.'
    };
  }
  if (status === 'sugestao_troca') {
    return {
      label: 'Troca sugerida',
      tone: 'border-amber-200 bg-amber-50 text-amber-700',
      hint: 'Já existe vínculo ativo diferente da recomendação.'
    };
  }
  if (status === 'ja_compativel') {
    return {
      label: 'Já compatível',
      tone: 'border-sky-200 bg-sky-50 text-sky-700',
      hint: 'O vínculo atual já está alinhado com a sugestão.'
    };
  }
  return {
    label: 'Sem match',
    tone: 'border-rose-200 bg-rose-50 text-rose-700',
    hint: 'A IA não encontrou um DCF seguro para atribuir.'
  };
};

const Vinculacoes = () => {
  const [vinculacoes, setVinculacoes] = useState([]);
  const [funcionarios, setFuncionarios] = useState([]);
  const [cargos, setCargos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('carteira');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [vinculacaoEditando, setVinculacaoEditando] = useState(null);
  const [calculandoScore, setCalculandoScore] = useState(false);
  const [batchSearch, setBatchSearch] = useState('');
  const [batchOnlyPending, setBatchOnlyPending] = useState(true);
  const [batchMaxItems, setBatchMaxItems] = useState(40);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([]);
  const [batchPreview, setBatchPreview] = useState([]);
  const [batchSummary, setBatchSummary] = useState(null);
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchConfirming, setBatchConfirming] = useState(false);

  const [formData, setFormData] = useState({
    employee_id: '',
    dcf_id: '',
    observacoes: '',
    match_score: null
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      await Promise.all([loadVinculacoes(), loadFuncionarios(), loadCargos()]);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadVinculacoes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/dcf/vinculacoes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVinculacoes(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar vinculações:', error);
      setVinculacoes([]);
    }
  };

  const loadFuncionarios = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/employees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const funcionariosAtivos = (response.data || []).filter((employee) => {
        const situacao = employee?.situacao?.toLowerCase();
        return !situacao || situacao === 'ativo';
      });
      setFuncionarios(funcionariosAtivos);
    } catch (error) {
      console.error('Erro ao carregar funcionários:', error);
      setFuncionarios([]);
    }
  };

  const loadCargos = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/dcf/cargos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCargos(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar cargos DCF:', error);
      setCargos([]);
    }
  };

  const resetForm = () => {
    setFormData({
      employee_id: '',
      dcf_id: '',
      observacoes: '',
      match_score: null
    });
  };

  const handleCreateVinculacao = async (event) => {
    event.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/dcf/vinculacoes`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowCreateModal(false);
      resetForm();
      await loadVinculacoes();
      alert('Vinculação DCF criada com sucesso.');
    } catch (error) {
      console.error('Erro ao criar vinculação:', error);
      alert(error.response?.data?.detail || 'Erro ao criar vinculação DCF.');
    }
  };

  const handleEditVinculacao = (vinculacao) => {
    setVinculacaoEditando(vinculacao);
    setFormData({
      employee_id: vinculacao.employee_id,
      dcf_id: vinculacao.dcf_id,
      observacoes: vinculacao.observacoes || '',
      match_score: vinculacao.match_score || null
    });
    setShowEditModal(true);
  };

  const handleUpdateVinculacao = async (event) => {
    event.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/api/dcf/vinculacoes/${vinculacaoEditando.id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowEditModal(false);
      setVinculacaoEditando(null);
      resetForm();
      await loadVinculacoes();
      alert('Vinculação atualizada com sucesso.');
    } catch (error) {
      console.error('Erro ao atualizar vinculação:', error);
      alert(error.response?.data?.detail || 'Erro ao atualizar vinculação.');
    }
  };

  const handleDeleteVinculacao = async (vinculacaoId) => {
    if (!window.confirm('Tem certeza que deseja remover esta vinculação?')) {
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/dcf/vinculacoes/${vinculacaoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await loadVinculacoes();
      alert('Vinculação removida com sucesso.');
    } catch (error) {
      console.error('Erro ao remover vinculação:', error);
      alert(error.response?.data?.detail || 'Erro ao remover vinculação.');
    }
  };

  const calcularMatchScoreIA = async () => {
    if (!formData.employee_id) {
      alert('Selecione um funcionário primeiro.');
      return;
    }
    if (!formData.dcf_id) {
      alert('Selecione uma DCF primeiro.');
      return;
    }

    setCalculandoScore(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/api/dcf/calcular-match-score`,
        {
          employee_id: formData.employee_id,
          dcf_id: formData.dcf_id
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data?.success) {
        setFormData((current) => ({
          ...current,
          match_score: response.data.match_score
        }));

        let mensagem = `Match Score: ${response.data.match_score}%\n\n${response.data.justificativa || ''}`;
        if (response.data.pontos_fortes?.length) {
          mensagem += '\n\nPontos fortes:\n';
          response.data.pontos_fortes.forEach((item) => {
            mensagem += `• ${item}\n`;
          });
        }
        if (response.data.pontos_atencao?.length) {
          mensagem += '\nPontos de atenção:\n';
          response.data.pontos_atencao.forEach((item) => {
            mensagem += `• ${item}\n`;
          });
        }
        alert(mensagem);
      }
    } catch (error) {
      console.error('Erro ao calcular match score:', error);
      alert(error.response?.data?.detail || 'Erro ao calcular match score.');
    } finally {
      setCalculandoScore(false);
    }
  };

  const getMatchScoreColor = (score) => {
    if (score >= 90) return 'text-emerald-700 bg-emerald-100';
    if (score >= 80) return 'text-sky-700 bg-sky-100';
    if (score >= 70) return 'text-amber-700 bg-amber-100';
    return 'text-rose-700 bg-rose-100';
  };

  const getMatchScoreIcon = (score) => {
    if (score >= 90) return <CheckCircle className="h-4 w-4" />;
    if (score >= 80) return <Star className="h-4 w-4" />;
    return <AlertTriangle className="h-4 w-4" />;
  };

  const filteredVinculacoes = vinculacoes.filter((vinculacao) => {
    const normalizedSearch = searchTerm.toLowerCase();
    const matchesSearch =
      !normalizedSearch ||
      vinculacao.employee_name?.toLowerCase().includes(normalizedSearch) ||
      vinculacao.dcf_name?.toLowerCase().includes(normalizedSearch);
    const statusAtual = vinculacao.status || (vinculacao.ativa ? 'ativa' : 'inativa');
    const matchesStatus = !filterStatus || statusAtual === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const vinculacoesAtivas = vinculacoes.filter((item) => item.ativa === true || item.status === 'ativa');
  const funcionariosComVinculoIds = new Set(vinculacoesAtivas.map((item) => item.employee_id));
  const funcionariosSemDcf = Math.max(0, funcionarios.length - funcionariosComVinculoIds.size);
  const matchScoreMedio = vinculacoesAtivas.length
    ? Math.round(vinculacoesAtivas.reduce((acc, item) => acc + (item.match_score || 0), 0) / vinculacoesAtivas.length)
    : 0;
  const vinculosAtencao = vinculacoesAtivas.filter((item) => (item.match_score || 0) < 80).length;
  const cargosCobertos = new Set(vinculacoesAtivas.map((item) => item.dcf_id)).size;

  const filteredBatchEmployees = funcionarios.filter((employee) => {
    const normalizedSearch = batchSearch.toLowerCase();
    const matchesSearch =
      !normalizedSearch ||
      getEmployeeName(employee).toLowerCase().includes(normalizedSearch) ||
      getEmployeeRole(employee).toLowerCase().includes(normalizedSearch) ||
      getEmployeeDepartment(employee).toLowerCase().includes(normalizedSearch);
    const matchesPending = !batchOnlyPending || !funcionariosComVinculoIds.has(employee.id);
    return matchesSearch && matchesPending;
  });
  const visibleBatchEmployees = filteredBatchEmployees.slice(0, 120);

  const toggleEmployeeSelection = (employeeId) => {
    setSelectedEmployeeIds((current) => (
      current.includes(employeeId)
        ? current.filter((id) => id !== employeeId)
        : [...current, employeeId]
    ));
  };

  const selectAllVisibleEmployees = () => {
    setSelectedEmployeeIds((current) => {
      const merged = new Set(current);
      visibleBatchEmployees.forEach((employee) => merged.add(employee.id));
      return Array.from(merged);
    });
  };

  const clearBatchSelection = () => {
    setSelectedEmployeeIds([]);
    setBatchPreview([]);
    setBatchSummary(null);
  };

  const handleGenerateBatchPreview = async () => {
    if (selectedEmployeeIds.length === 0) {
      alert('Selecione pelo menos um colaborador para analisar.');
      return;
    }

    setBatchLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/api/dcf/vinculacoes/lote/prever`,
        {
          employee_ids: selectedEmployeeIds,
          max_items: batchMaxItems
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const items = (response.data?.items || []).map((item) => ({
        ...item,
        selected: item.status === 'novo',
        force_replace: false,
        observacoes: item.justificativa || ''
      }));

      setBatchPreview(items);
      setBatchSummary(response.data?.summary || null);

      if (response.data?.summary?.processados_parcialmente) {
        alert(`A prévia foi limitada a ${batchMaxItems} colaboradores nesta rodada.`);
      }
    } catch (error) {
      console.error('Erro ao gerar prévia em lote:', error);
      alert(error.response?.data?.detail || 'Erro ao gerar prévia em lote.');
    } finally {
      setBatchLoading(false);
    }
  };

  const handleTogglePreviewSelection = (employeeId) => {
    setBatchPreview((current) => current.map((item) => (
      item.employee_id === employeeId
        ? { ...item, selected: !item.selected }
        : item
    )));
  };

  const handleTogglePreviewReplace = (employeeId) => {
    setBatchPreview((current) => current.map((item) => (
      item.employee_id === employeeId
        ? { ...item, force_replace: !item.force_replace }
        : item
    )));
  };

  const handleConfirmBatch = async () => {
    const selectedItems = batchPreview.filter((item) => item.selected && item.suggested_dcf_id);
    if (selectedItems.length === 0) {
      alert('Selecione ao menos uma sugestão válida para confirmar.');
      return;
    }

    const invalidReplace = selectedItems.find((item) => item.status === 'sugestao_troca' && !item.force_replace);
    if (invalidReplace) {
      alert(`Marque a opção de substituir o vínculo atual para ${invalidReplace.employee_name}.`);
      return;
    }

    setBatchConfirming(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/api/dcf/vinculacoes/lote/confirmar`,
        {
          items: selectedItems.map((item) => ({
            employee_id: item.employee_id,
            dcf_id: item.suggested_dcf_id,
            match_score: item.confidence,
            observacoes: item.observacoes,
            force_replace: item.status === 'sugestao_troca' ? item.force_replace : false
          }))
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      await loadVinculacoes();
      setSelectedEmployeeIds([]);
      setBatchPreview([]);
      setBatchSummary(null);

      const summary = response.data?.summary || {};
      alert(
        `Processamento concluído.\n\n` +
        `Criados: ${summary.created || 0}\n` +
        `Substituídos: ${summary.replaced || 0}\n` +
        `Ignorados: ${summary.skipped || 0}\n` +
        `Erros: ${summary.errors || 0}`
      );
    } catch (error) {
      console.error('Erro ao confirmar vínculos em lote:', error);
      alert(error.response?.data?.detail || 'Erro ao confirmar vínculos em lote.');
    } finally {
      setBatchConfirming(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-1/4 rounded bg-slate-200" />
          <div className="grid gap-4">
            {[...Array(5)].map((_, index) => (
              <div key={index} className="h-24 rounded bg-slate-200" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <section className="overflow-hidden rounded-[28px] bg-gradient-to-br from-slate-950 via-slate-900 to-amber-900 text-white shadow-xl">
        <div className="grid gap-8 p-6 lg:grid-cols-[1.45fr_1fr] lg:p-8">
          <div className="space-y-5">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-medium uppercase tracking-[0.24em] text-amber-100">
              <Link className="h-3.5 w-3.5" />
              Vinculações DCF
            </div>
            <div>
              <h1 className="text-3xl font-semibold tracking-tight lg:text-4xl">Estrutura boa precisa refletir o cargo real de quem opera.</h1>
              <p className="mt-3 max-w-2xl text-sm text-slate-200 lg:text-base">
                Aqui você mantém a carteira atual sob controle e também consegue atribuir DCFs em lote a partir do cargo do colaborador, com apoio de IA e revisão humana antes da confirmação.
              </p>
            </div>
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              <button
                type="button"
                onClick={() => setActiveTab('carteira')}
                className={`rounded-2xl px-4 py-4 text-left transition-transform hover:-translate-y-0.5 ${
                  activeTab === 'carteira' ? 'bg-white text-slate-900' : 'border border-white/15 bg-white/10 text-white hover:bg-white/15'
                }`}
              >
                <BarChart3 className="h-5 w-5" />
                <p className="mt-5 text-base font-semibold">Carteira atual</p>
                <p className={`mt-1 text-sm ${activeTab === 'carteira' ? 'text-slate-600' : 'text-slate-200'}`}>Revisar vínculos ativos, score e ajustes individuais.</p>
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('lote')}
                className={`rounded-2xl px-4 py-4 text-left transition-transform hover:-translate-y-0.5 ${
                  activeTab === 'lote' ? 'bg-white text-slate-900' : 'border border-white/15 bg-white/10 text-white hover:bg-white/15'
                }`}
              >
                <Wand2 className="h-5 w-5" />
                <p className="mt-5 text-base font-semibold">Atribuição em lote</p>
                <p className={`mt-1 text-sm ${activeTab === 'lote' ? 'text-slate-600' : 'text-slate-200'}`}>Selecionar colaboradores, pedir sugestão e confirmar em massa.</p>
              </button>
              <button
                type="button"
                onClick={() => setShowCreateModal(true)}
                className="rounded-2xl border border-white/15 bg-white/10 px-4 py-4 text-left hover:bg-white/15"
              >
                <Plus className="h-5 w-5" />
                <p className="mt-5 text-base font-semibold">Nova vinculação</p>
                <p className="mt-1 text-sm text-slate-200">Criar um vínculo manual quando precisar tratar exceções.</p>
              </button>
              <div className="rounded-2xl border border-white/15 bg-white/10 px-4 py-4">
                <Target className="h-5 w-5" />
                <p className="mt-5 text-base font-semibold">Atenção</p>
                <p className="mt-1 text-sm text-slate-200">{vinculosAtencao} vínculo(s) com score abaixo de 80%.</p>
              </div>
            </div>
          </div>

          <div className="rounded-[24px] border border-white/10 bg-white/10 p-5 backdrop-blur">
            <p className="text-sm font-medium text-slate-200">Panorama das conexões</p>
            <div className="mt-5 space-y-3 text-sm text-slate-100">
              <div className="flex items-center justify-between rounded-2xl bg-white/10 px-4 py-3">
                <span>Vínculos ativos</span>
                <strong>{vinculacoesAtivas.length}</strong>
              </div>
              <div className="flex items-center justify-between rounded-2xl bg-white/10 px-4 py-3">
                <span>Funcionários sem DCF</span>
                <strong>{funcionariosSemDcf}</strong>
              </div>
              <div className="flex items-center justify-between rounded-2xl bg-white/10 px-4 py-3">
                <span>Cargos cobertos</span>
                <strong>{cargosCobertos}</strong>
              </div>
              <div className="flex items-center justify-between rounded-2xl bg-emerald-300/20 px-4 py-3 text-emerald-100">
                <span>Match médio</span>
                <strong>{matchScoreMedio}%</strong>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 gap-4 xl:grid-cols-4">
        <article className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-sm font-medium text-slate-500">Total de vínculos</p>
          <p className="mt-2 text-3xl font-semibold text-slate-900">{vinculacoes.length}</p>
        </article>
        <article className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-sm font-medium text-slate-500">Ativos</p>
          <p className="mt-2 text-3xl font-semibold text-slate-900">{vinculacoesAtivas.length}</p>
        </article>
        <article className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-sm font-medium text-slate-500">Sem DCF</p>
          <p className="mt-2 text-3xl font-semibold text-slate-900">{funcionariosSemDcf}</p>
        </article>
        <article className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-sm font-medium text-slate-500">Match médio</p>
          <p className="mt-2 text-3xl font-semibold text-slate-900">{matchScoreMedio}%</p>
        </article>
      </section>

      {activeTab === 'carteira' && (
        <>
          <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <p className="text-sm font-medium uppercase tracking-[0.18em] text-slate-400">Triagem</p>
                <h2 className="mt-2 text-2xl font-semibold text-slate-900">Buscar e priorizar vínculos</h2>
                <p className="mt-2 text-sm text-slate-600">Use a busca por colaborador ou DCF e restrinja por status quando precisar revisar uma carteira específica.</p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setSearchTerm('');
                  setFilterStatus('');
                }}
                className="inline-flex items-center justify-center rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Limpar filtros
              </button>
            </div>

            <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                <label className="mb-2 flex items-center gap-2 text-sm font-medium text-slate-700">
                  <Search className="h-4 w-4 text-slate-400" />
                  Buscar funcionário ou DCF
                </label>
                <input
                  type="text"
                  placeholder="Ex: analista, joão, RH..."
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                <label className="mb-2 flex items-center gap-2 text-sm font-medium text-slate-700">
                  <Filter className="h-4 w-4 text-slate-400" />
                  Status da vinculação
                </label>
                <select
                  value={filterStatus}
                  onChange={(event) => setFilterStatus(event.target.value)}
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                >
                  <option value="">Todos os status</option>
                  <option value="ativa">Ativas</option>
                  <option value="inativa">Inativas</option>
                </select>
              </div>
            </div>
          </section>

          <section className="rounded-3xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-200 px-6 py-5">
              <h2 className="text-lg font-semibold text-slate-900">Carteira de vinculações</h2>
              <p className="mt-1 text-sm text-slate-500">{filteredVinculacoes.length} vínculo(s) encontrados para a visão atual.</p>
            </div>

            <div className="divide-y divide-slate-200">
              {filteredVinculacoes.length === 0 ? (
                <div className="p-12 text-center">
                  <Link className="mx-auto mb-4 h-12 w-12 text-slate-300" />
                  <p className="text-lg text-slate-500">Nenhuma vinculação encontrada</p>
                  <p className="mt-1 text-sm text-slate-400">Ajuste os filtros ou crie uma nova vinculação.</p>
                </div>
              ) : (
                filteredVinculacoes.map((vinculacao) => (
                  <div key={vinculacao.id} className="p-6 hover:bg-slate-50">
                    <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
                      <div className="flex-1 space-y-4">
                        <div className="flex flex-col gap-4 xl:flex-row xl:items-center">
                          <div className="flex items-center space-x-3">
                            <div className="rounded-2xl bg-sky-100 p-3">
                              <User className="h-5 w-5 text-sky-700" />
                            </div>
                            <div>
                              <h3 className="text-lg font-medium text-slate-900">{vinculacao.employee_name}</h3>
                              <p className="text-sm text-slate-600">{vinculacao.employee_position}</p>
                            </div>
                          </div>

                          <div className="hidden h-px flex-1 bg-slate-200 xl:block" />

                          <div className="flex items-center space-x-3">
                            <div className="rounded-2xl bg-emerald-100 p-3">
                              <Briefcase className="h-5 w-5 text-emerald-700" />
                            </div>
                            <div>
                              <h3 className="text-lg font-medium text-slate-900">{vinculacao.dcf_name}</h3>
                              <p className="text-sm capitalize text-slate-600">Nível: {vinculacao.dcf_nivel || 'não definido'}</p>
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3 text-sm">
                            <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Data de vínculo</p>
                            <div className="mt-1 flex items-center gap-1 text-slate-800">
                              <Calendar className="h-4 w-4 text-slate-400" />
                              <span>{formatDate(vinculacao.data_vinculacao)}</span>
                            </div>
                          </div>
                          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3 text-sm">
                            <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Status</p>
                            <p className="mt-1 font-medium capitalize text-slate-800">{vinculacao.status || (vinculacao.ativa ? 'ativa' : 'inativa')}</p>
                          </div>
                          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3 text-sm">
                            <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Observação</p>
                            <p className="mt-1 truncate text-slate-700">{vinculacao.observacoes || 'Sem observações cadastradas'}</p>
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-col gap-3 xl:min-w-[220px] xl:items-end">
                        <div className={`flex items-center space-x-2 rounded-2xl px-4 py-3 ${getMatchScoreColor(vinculacao.match_score || 0)}`}>
                          {getMatchScoreIcon(vinculacao.match_score || 0)}
                          <span className="font-medium">{vinculacao.match_score || 0}% de aderência</span>
                        </div>

                        <div className="flex items-center space-x-2">
                          <button
                            type="button"
                            onClick={() => handleEditVinculacao(vinculacao)}
                            className="rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-100"
                          >
                            Editar
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteVinculacao(vinculacao.id)}
                            className="rounded-full border border-red-200 px-4 py-2 text-sm font-medium text-red-600 transition-colors hover:bg-red-50"
                          >
                            Remover
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </section>
        </>
      )}

      {activeTab === 'lote' && (
        <>
          <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <p className="text-sm font-medium uppercase tracking-[0.18em] text-slate-400">Batch assignment</p>
                <h2 className="mt-2 text-2xl font-semibold text-slate-900">Atribuir colaboradores ao DCF com revisão antes da gravação</h2>
                <p className="mt-2 max-w-3xl text-sm text-slate-600">
                  A seleção usa o cargo atual do colaborador, passa pela IA e propõe o DCF mais aderente. Quando já existir vínculo, a troca só acontece se você autorizar explicitamente.
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={clearBatchSelection}
                  className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  <RefreshCw className="h-4 w-4" />
                  Limpar seleção
                </button>
                <button
                  type="button"
                  onClick={handleGenerateBatchPreview}
                  disabled={batchLoading}
                  className="inline-flex items-center gap-2 rounded-full bg-amber-500 px-4 py-2 text-sm font-medium text-slate-950 transition-colors hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <Sparkles className="h-4 w-4" />
                  {batchLoading ? 'Analisando...' : 'Gerar sugestões'}
                </button>
              </div>
            </div>

            <div className="mt-6 grid gap-4 lg:grid-cols-[1.2fr_0.8fr_0.6fr]">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                <label className="mb-2 flex items-center gap-2 text-sm font-medium text-slate-700">
                  <Search className="h-4 w-4 text-slate-400" />
                  Buscar colaborador, cargo ou departamento
                </label>
                <input
                  type="text"
                  value={batchSearch}
                  onChange={(event) => setBatchSearch(event.target.value)}
                  placeholder="Ex: analista, financeiro, joão..."
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                <label className="mb-2 flex items-center gap-2 text-sm font-medium text-slate-700">
                  <Filter className="h-4 w-4 text-slate-400" />
                  Regras da lista
                </label>
                <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">
                  <span>Mostrar apenas pendentes</span>
                  <input
                    type="checkbox"
                    checked={batchOnlyPending}
                    onChange={(event) => setBatchOnlyPending(event.target.checked)}
                    className="h-4 w-4 rounded border-slate-300 text-amber-500 focus:ring-amber-500"
                  />
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                <label className="mb-2 flex items-center gap-2 text-sm font-medium text-slate-700">
                  <Users className="h-4 w-4 text-slate-400" />
                  Limite por rodada
                </label>
                <select
                  value={batchMaxItems}
                  onChange={(event) => setBatchMaxItems(Number(event.target.value))}
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                >
                  <option value={20}>20 colaboradores</option>
                  <option value={40}>40 colaboradores</option>
                  <option value={80}>80 colaboradores</option>
                </select>
              </div>
            </div>

            <div className="mt-6 grid gap-4 md:grid-cols-3">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <p className="text-sm text-slate-500">Filtrados agora</p>
                <p className="mt-2 text-2xl font-semibold text-slate-900">{filteredBatchEmployees.length}</p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <p className="text-sm text-slate-500">Selecionados</p>
                <p className="mt-2 text-2xl font-semibold text-slate-900">{selectedEmployeeIds.length}</p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <p className="text-sm text-slate-500">Sem DCF ativo</p>
                <p className="mt-2 text-2xl font-semibold text-slate-900">{funcionariosSemDcf}</p>
              </div>
            </div>
          </section>

          <section className="rounded-3xl border border-slate-200 bg-white shadow-sm">
            <div className="flex flex-col gap-3 border-b border-slate-200 px-6 py-5 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Colaboradores elegíveis</h2>
                <p className="mt-1 text-sm text-slate-500">
                  {visibleBatchEmployees.length} colaborador(es) exibidos nesta página. A lista visual é limitada para manter a tela fluida.
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={selectAllVisibleEmployees}
                  className="rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  Selecionar visíveis
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedEmployeeIds([])}
                  className="rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  Limpar seleção
                </button>
              </div>
            </div>

            {visibleBatchEmployees.length === 0 ? (
              <div className="p-10 text-center">
                <Users className="mx-auto h-12 w-12 text-slate-300" />
                <p className="mt-4 text-lg text-slate-500">Nenhum colaborador encontrado</p>
                <p className="mt-1 text-sm text-slate-400">Ajuste os filtros para montar outra rodada de atribuição.</p>
              </div>
            ) : (
              <div className="grid gap-4 p-6 md:grid-cols-2 2xl:grid-cols-3">
                {visibleBatchEmployees.map((employee) => {
                  const selected = selectedEmployeeIds.includes(employee.id);
                  const hasVinculo = funcionariosComVinculoIds.has(employee.id);
                  return (
                    <button
                      key={employee.id}
                      type="button"
                      onClick={() => toggleEmployeeSelection(employee.id)}
                      className={`rounded-3xl border p-5 text-left transition ${
                        selected
                          ? 'border-amber-400 bg-amber-50 shadow-sm'
                          : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-lg font-semibold text-slate-900">{getEmployeeName(employee)}</p>
                          <p className="mt-1 text-sm text-slate-600">{getEmployeeRole(employee)}</p>
                        </div>
                        <span className={`rounded-full px-3 py-1 text-xs font-medium ${hasVinculo ? 'bg-sky-100 text-sky-700' : 'bg-emerald-100 text-emerald-700'}`}>
                          {hasVinculo ? 'Já vinculado' : 'Sem DCF'}
                        </span>
                      </div>
                      <div className="mt-4 space-y-2 text-sm text-slate-600">
                        <p><span className="font-medium text-slate-800">Departamento:</span> {getEmployeeDepartment(employee)}</p>
                        <p><span className="font-medium text-slate-800">CBO:</span> {employee.cbo || 'Não informado'}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </section>

          {batchPreview.length > 0 && (
            <section className="rounded-3xl border border-slate-200 bg-white shadow-sm">
              <div className="flex flex-col gap-4 border-b border-slate-200 px-6 py-5 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Prévia das sugestões</h2>
                  <p className="mt-1 text-sm text-slate-500">
                    Revise o que será criado ou substituído antes de gravar.
                  </p>
                </div>
                <div className="flex flex-wrap gap-3">
                  <div className="rounded-full border border-slate-200 px-4 py-2 text-sm text-slate-700">
                    Selecionadas para gravar: {batchPreview.filter((item) => item.selected).length}
                  </div>
                  <button
                    type="button"
                    onClick={handleConfirmBatch}
                    disabled={batchConfirming}
                    className="rounded-full bg-slate-950 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {batchConfirming ? 'Confirmando...' : 'Confirmar vínculos'}
                  </button>
                </div>
              </div>

              {batchSummary && (
                <div className="grid gap-4 border-b border-slate-200 px-6 py-5 md:grid-cols-4">
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <p className="text-sm text-slate-500">Novos</p>
                    <p className="mt-2 text-2xl font-semibold text-slate-900">{batchSummary.novos || 0}</p>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <p className="text-sm text-slate-500">Trocas sugeridas</p>
                    <p className="mt-2 text-2xl font-semibold text-slate-900">{batchSummary.trocas_sugeridas || 0}</p>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <p className="text-sm text-slate-500">Já compatíveis</p>
                    <p className="mt-2 text-2xl font-semibold text-slate-900">{batchSummary.ja_compativeis || 0}</p>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <p className="text-sm text-slate-500">Sem match</p>
                    <p className="mt-2 text-2xl font-semibold text-slate-900">{batchSummary.sem_match || 0}</p>
                  </div>
                </div>
              )}

              <div className="grid gap-4 p-6 xl:grid-cols-2">
                {batchPreview.map((item) => {
                  const meta = getBatchStatusMeta(item.status);
                  const selectable = item.status === 'novo' || item.status === 'sugestao_troca';
                  return (
                    <article key={item.employee_id} className="rounded-3xl border border-slate-200 bg-slate-50 p-5">
                      <div className="flex flex-col gap-4">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <p className="text-lg font-semibold text-slate-900">{item.employee_name}</p>
                            <p className="mt-1 text-sm text-slate-600">{item.employee_cargo}</p>
                          </div>
                          <span className={`rounded-full border px-3 py-1 text-xs font-medium ${meta.tone}`}>
                            {meta.label}
                          </span>
                        </div>

                        <div className="grid gap-3 md:grid-cols-2">
                          <div className="rounded-2xl border border-slate-200 bg-white p-4">
                            <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Vínculo atual</p>
                            <p className="mt-2 text-sm font-medium text-slate-900">{item.current_vinculacao?.dcf_name || 'Sem DCF ativo'}</p>
                            <p className="mt-1 text-sm text-slate-600">{item.current_vinculacao?.dcf_cbo || 'Sem CBO vinculado'}</p>
                          </div>
                          <div className="rounded-2xl border border-slate-200 bg-white p-4">
                            <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Sugestão</p>
                            <p className="mt-2 text-sm font-medium text-slate-900">{item.suggested_dcf_name || 'Sem DCF sugerido'}</p>
                            <p className="mt-1 text-sm text-slate-600">{item.suggested_dcf_cbo || 'Sem CBO sugerido'}</p>
                          </div>
                        </div>

                        <div className="grid gap-3 md:grid-cols-[0.7fr_1.3fr]">
                          <div className="rounded-2xl border border-slate-200 bg-white p-4">
                            <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Confiança</p>
                            <div className={`mt-2 inline-flex items-center gap-2 rounded-full px-3 py-2 text-sm font-medium ${getMatchScoreColor(item.confidence || 0)}`}>
                              {getMatchScoreIcon(item.confidence || 0)}
                              {(item.confidence || 0)}%
                            </div>
                            <p className="mt-2 text-xs text-slate-500">{meta.hint}</p>
                          </div>
                          <div className="rounded-2xl border border-slate-200 bg-white p-4">
                            <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Justificativa</p>
                            <p className="mt-2 text-sm text-slate-700">{item.justificativa || 'Sem justificativa retornada.'}</p>
                            <p className="mt-2 text-xs uppercase tracking-wide text-slate-400">Fonte: {item.source || 'heuristica'}</p>
                          </div>
                        </div>

                        <div className="space-y-3 rounded-2xl border border-slate-200 bg-white p-4">
                          <label className={`flex items-center justify-between gap-3 text-sm ${selectable ? 'text-slate-800' : 'text-slate-400'}`}>
                            <span>Confirmar esta sugestão</span>
                            <input
                              type="checkbox"
                              checked={!!item.selected}
                              onChange={() => handleTogglePreviewSelection(item.employee_id)}
                              disabled={!selectable}
                              className="h-4 w-4 rounded border-slate-300 text-amber-500 focus:ring-amber-500"
                            />
                          </label>

                          {item.status === 'sugestao_troca' && (
                            <label className="flex items-center justify-between gap-3 text-sm text-slate-800">
                              <span>Substituir o vínculo atual ao confirmar</span>
                              <input
                                type="checkbox"
                                checked={!!item.force_replace}
                                onChange={() => handleTogglePreviewReplace(item.employee_id)}
                                className="h-4 w-4 rounded border-slate-300 text-amber-500 focus:ring-amber-500"
                              />
                            </label>
                          )}
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            </section>
          )}
        </>
      )}

      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/55 p-4 backdrop-blur-sm">
          <div className="w-full max-w-xl overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-2xl">
            <div className="bg-gradient-to-r from-slate-950 via-slate-900 to-amber-900 px-6 py-5 text-white">
              <h2 className="text-2xl font-semibold">Nova Vinculação DCF</h2>
              <p className="mt-1 text-sm text-amber-100">Conecte o colaborador à estrutura certa e registre o contexto dessa aderência.</p>
            </div>
            <div className="relative p-6">
              <button
                type="button"
                onClick={() => setShowCreateModal(false)}
                className="absolute right-6 top-6 text-slate-400 hover:text-slate-600"
              >
                ✕
              </button>

              <form onSubmit={handleCreateVinculacao} className="space-y-4">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <label className="mb-1 block text-sm font-medium text-slate-700">Funcionário*</label>
                  <select
                    required
                    value={formData.employee_id}
                    onChange={(event) => setFormData({ ...formData, employee_id: event.target.value })}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500"
                  >
                    <option value="">Selecione o funcionário</option>
                    {funcionarios.map((funcionario) => (
                      <option key={funcionario.id} value={funcionario.id}>
                        {getEmployeeName(funcionario)} - {getEmployeeRole(funcionario)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <label className="mb-1 block text-sm font-medium text-slate-700">DCF (Cargo)*</label>
                  <select
                    required
                    value={formData.dcf_id}
                    onChange={(event) => setFormData({ ...formData, dcf_id: event.target.value })}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500"
                  >
                    <option value="">Selecione o cargo</option>
                    {cargos.map((cargo) => (
                      <option key={cargo.id} value={cargo.id}>
                        {cargo.nome_cargo} - {cargo.departamento}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <label className="mb-1 block text-sm font-medium text-slate-700">Observações</label>
                  <textarea
                    value={formData.observacoes}
                    onChange={(event) => setFormData({ ...formData, observacoes: event.target.value })}
                    rows={3}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500"
                    placeholder="Observações sobre a vinculação..."
                  />
                </div>

                <div className="flex justify-end space-x-3 border-t border-slate-200 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="rounded-full border border-slate-300 px-4 py-2 text-slate-700 hover:bg-slate-50"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="rounded-full bg-amber-500 px-4 py-2 text-slate-950 hover:bg-amber-400"
                  >
                    Criar vinculação
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {showEditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/55 p-4 backdrop-blur-sm">
          <div className="w-full max-w-2xl overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-2xl">
            <div className="bg-gradient-to-r from-slate-950 via-slate-900 to-amber-900 px-6 py-4 text-white">
              <h2 className="text-xl font-semibold">Editar Vinculação DCF</h2>
              <p className="mt-1 text-sm text-amber-100">Atualize o cargo vinculado, o score de aderência e o contexto operacional.</p>
            </div>

            <form onSubmit={handleUpdateVinculacao} className="space-y-4 p-6">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <label className="mb-1 block text-sm font-medium text-slate-700">Funcionário* (não editável)</label>
                <select
                  value={formData.employee_id}
                  disabled
                  className="w-full cursor-not-allowed rounded-lg border border-slate-300 bg-slate-100 px-3 py-2"
                >
                  <option value="">Selecione...</option>
                  {funcionarios.map((funcionario) => (
                    <option key={funcionario.id} value={funcionario.id}>
                      {getEmployeeName(funcionario)} - {getEmployeeRole(funcionario)}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-slate-500">Para alterar o colaborador, crie uma nova vinculação.</p>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <label className="mb-1 block text-sm font-medium text-slate-700">DCF (Cargo)*</label>
                <select
                  required
                  value={formData.dcf_id}
                  onChange={(event) => setFormData({ ...formData, dcf_id: event.target.value })}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500"
                >
                  <option value="">Selecione...</option>
                  {cargos.map((cargo) => (
                    <option key={cargo.id} value={cargo.id}>
                      {cargo.nome_cargo} - {cargo.departamento}
                    </option>
                  ))}
                </select>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <label className="mb-1 flex items-center justify-between text-sm font-medium text-slate-700">
                  <span>Match Score (%)</span>
                  <button
                    type="button"
                    onClick={calcularMatchScoreIA}
                    disabled={calculandoScore || !formData.employee_id || !formData.dcf_id}
                    className="rounded-lg bg-slate-950 px-3 py-1 text-xs text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {calculandoScore ? 'Calculando...' : 'Calcular com IA'}
                  </button>
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={formData.match_score || ''}
                  onChange={(event) => setFormData({
                    ...formData,
                    match_score: event.target.value ? parseInt(event.target.value, 10) : null
                  })}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500"
                  placeholder="0-100"
                />
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <label className="mb-1 block text-sm font-medium text-slate-700">Observações</label>
                <textarea
                  value={formData.observacoes}
                  onChange={(event) => setFormData({ ...formData, observacoes: event.target.value })}
                  rows={3}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500"
                  placeholder="Observações sobre esta vinculação..."
                />
              </div>

              <div className="flex justify-end space-x-3 border-t border-slate-200 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setVinculacaoEditando(null);
                    resetForm();
                  }}
                  className="rounded-full border border-slate-300 px-4 py-2 text-slate-700 hover:bg-slate-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="rounded-full bg-amber-500 px-4 py-2 text-slate-950 hover:bg-amber-400"
                >
                  Salvar alterações
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Vinculacoes;
